import type {
  PerformanceDataPoint,
  PerformanceHistoryRow,
} from "@/components/fund/leveraged-perf-chart";
import type { PremiumDiscountQuarterlyRow } from "@/components/fund/leveraged-premium-discount";
/**
 * Pure transform functions for the premium/discount and performance charts.
 *
 * Ported verbatim from dane `frontend/hooks/use-etf-live-nav.ts`.
 * No React — these are pure functions consumed by the React-Query hook.
 *
 * Note: dane's ApiDailyNavRecord fields are non-null; etfsite's are nullable
 * (`number | string | null`). `toNum` handles null → 0.
 */
import type { ApiDailyNavRecord } from "@/data/etf-api";
import type { PdDataPoint } from "@/hooks/use-etf-live-nav";

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function toNum(v: number | string | null | undefined): number {
  if (v == null) return 0;
  return typeof v === "number" ? v : Number.parseFloat(v as string) || 0;
}

export function fmtShortDate(dateStr: string): string {
  const d = new Date(`${dateStr.split("T")[0]}T12:00:00`);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// ── Performance returns ────────────────────────────────────────────────

export function computePerformanceHistory(records: ApiDailyNavRecord[]): PerformanceHistoryRow[] {
  const empty: PerformanceHistoryRow = {
    label: "",
    oneMonth: "—",
    threeMonth: "—",
    ytd: "—",
    oneYear: "—",
    threeYear: "—",
    fiveYear: "—",
    sinceInceptionAnn: "—",
    sinceInceptionCum: "—",
  };

  if (records.length < 2) {
    return [
      { ...empty, label: "NAV" },
      { ...empty, label: "Market Price" },
    ];
  }

  const latest = records[records.length - 1]!;
  const latestDate = new Date(`${latest.rate_date.split("T")[0]}T12:00:00`);
  const latestNav = toNum(latest.nav);
  const latestMkt = toNum(latest.market_price);

  // Records are date-ascending; find the record on or before targetStr.
  function recordOnOrBefore(targetStr: string): ApiDailyNavRecord | null {
    let hit: ApiDailyNavRecord | null = null;
    for (const r of records) {
      const rd = r.rate_date.split("T")[0]!;
      if (rd <= targetStr) hit = r;
      else break;
    }
    return hit;
  }

  function pctChange(from: number, to: number): string {
    if (!Number.isFinite(from) || from === 0 || !Number.isFinite(to)) return "—";
    const ret = ((to - from) / from) * 100;
    return `${ret >= 0 ? "+" : ""}${ret.toFixed(2)}%`;
  }

  function currentFor(field: "nav" | "market_price"): number {
    return field === "nav" ? latestNav : latestMkt;
  }

  function returnOver(field: "nav" | "market_price", days: number): string {
    const target = new Date(latestDate);
    target.setDate(target.getDate() - days);
    const targetStr = target.toISOString().split("T")[0]!;
    const earliest = records[0]?.rate_date.split("T")[0]!;
    if (targetStr < earliest) return "—";
    const closest = recordOnOrBefore(targetStr);
    if (!closest) return "—";
    return pctChange(toNum(closest[field]), currentFor(field));
  }

  function returnSinceInception(field: "nav" | "market_price"): string {
    return pctChange(toNum(records[0]?.[field]), currentFor(field));
  }

  function returnYtd(field: "nav" | "market_price"): string {
    const yearStart = new Date(latestDate.getFullYear(), 0, 1);
    const yearStartStr = yearStart.toISOString().split("T")[0]!;
    const earliest = records[0]?.rate_date.split("T")[0]!;
    // Fund launched mid-year → YTD === since-inception.
    if (yearStartStr < earliest) return returnSinceInception(field);
    let hit: ApiDailyNavRecord | null = null;
    for (const r of records) {
      const rd = r.rate_date.split("T")[0]!;
      if (rd >= yearStartStr) {
        hit = r;
        break;
      }
    }
    if (!hit) return "—";
    return pctChange(toNum(hit[field]), currentFor(field));
  }

  function returnSinceInceptionAnn(field: "nav" | "market_price"): string {
    const baseVal = toNum(records[0]?.[field]);
    const currentVal = currentFor(field);
    if (!Number.isFinite(baseVal) || baseVal === 0 || !Number.isFinite(currentVal)) return "—";
    const firstDate = new Date(`${records[0]?.rate_date.split("T")[0]}T12:00:00`);
    const days = (latestDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);
    const years = days / 365.25;
    if (years <= 0) return "—";
    // For sub-1y windows the SEC convention is to show the cumulative
    // figure rather than annualizing; matches static fact-sheets in this repo.
    const total = currentVal / baseVal;
    const ret = years >= 1 ? total ** (1 / years) - 1 : total - 1;
    const pct = ret * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
  }

  function rowFor(field: "nav" | "market_price", label: string): PerformanceHistoryRow {
    return {
      label,
      oneMonth: returnOver(field, 30),
      threeMonth: returnOver(field, 91),
      ytd: returnYtd(field),
      oneYear: returnOver(field, 365),
      threeYear: returnOver(field, 365 * 3),
      fiveYear: returnOver(field, 365 * 5),
      sinceInceptionAnn: returnSinceInceptionAnn(field),
      sinceInceptionCum: returnSinceInception(field),
    };
  }

  return [rowFor("nav", "NAV"), rowFor("market_price", "Market Price")];
}

// ── Premium/Discount chart points ──────────────────────────────────────

export function buildPdChart(records: ApiDailyNavRecord[]): {
  chartPoints: string;
  dateLabels: string[];
  dataPoints: PdDataPoint[];
} {
  if (records.length === 0) {
    return { chartPoints: "20,80 780,80", dateLabels: [], dataPoints: [] };
  }

  // SVG viewBox is 800 x 160. Y=80 is the zero line.
  // Scale: ±1% maps to ±40px
  const xStart = 20;
  const xEnd = 780;
  const yZero = 80;
  const yScale = 40;

  const n = records.length;
  const points: string[] = [];
  const dataPoints: PdDataPoint[] = [];

  for (let i = 0; i < n; i++) {
    const x = xStart + (i / Math.max(n - 1, 1)) * (xEnd - xStart);
    const pd = toNum(records[i]?.premium_discount_percentage);
    const y = Math.max(10, Math.min(150, yZero - pd * yScale));
    points.push(`${Math.round(x)},${Math.round(y)}`);
    dataPoints.push({
      date: records[i]?.rate_date.split("T")[0] ?? records[i]?.rate_date ?? "",
      pd,
      nav: toNum(records[i]?.nav),
      mkt: toNum(records[i]?.market_price),
    });
  }

  const dateLabels: string[] = [];
  for (let i = 0; i < 5; i++) {
    const idx = Math.round((i / 4) * (n - 1));
    dateLabels.push(fmtShortDate(records[idx]?.rate_date ?? ""));
  }

  return {
    chartPoints: points.join(" "),
    dateLabels,
    dataPoints,
  };
}

// ── Quarterly P/D table ────────────────────────────────────────────────

export function buildQuarterlyTable(records: ApiDailyNavRecord[]): PremiumDiscountQuarterlyRow[] {
  // Group records by quarter
  const quarters = new Map<string, ApiDailyNavRecord[]>();

  for (const r of records) {
    const d = new Date(`${r.rate_date.split("T")[0]}T12:00:00`);
    const q = Math.floor(d.getMonth() / 3) + 1;
    const key = `Q${q} ${d.getFullYear()}`;
    const arr = quarters.get(key);
    if (arr) arr.push(r);
    else quarters.set(key, [r]);
  }

  const rows: PremiumDiscountQuarterlyRow[] = [];

  for (const [quarter, recs] of quarters) {
    let atNav = 0;
    let atPremium = 0;
    let atDiscount = 0;
    // The 5th column is "P/D +/- 2.00%" — the count of trading days where
    // |premium/discount| exceeded 2.00%. Per the SEC ETF rule the table
    // tracks how often the fund traded outside the ±2% NAV band.
    let outside2pct = 0;

    for (const r of recs) {
      const pd = toNum(r.premium_discount_percentage);
      if (Math.abs(pd) < 0.005) atNav++;
      else if (pd > 0) atPremium++;
      else atDiscount++;
      if (Math.abs(pd) > 2.0) outside2pct++;
    }

    rows.push({
      quarter,
      daysAtNav: String(atNav),
      daysAtPremium: String(atPremium),
      daysAtDiscount: String(atDiscount),
      pdRange: String(outside2pct),
    });
  }

  return rows;
}
